import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CreateUserRequest, UserRole, Agent, Client } from '../types';

interface CreateUserFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
}

const CreateUserForm: React.FC<CreateUserFormProps> = ({ onSuccess, onCancel, onClose }) => {
  const { user, token, hasRole } = useAuth();
  const [formData, setFormData] = useState<CreateUserRequest>({
    username: '',
    password: '',
    role: 'user',
    agentIds: [],
    clientIds: [],
  });
  const [agents, setAgents] = useState<Agent[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Загружаем список агентов и клиентов для привязки
    const fetchData = async () => {
      try {
        const [agentsRes, clientsRes] = await Promise.all([
          fetch('/api/agents', {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
          fetch('/api/clients', {
            headers: { 'Authorization': `Bearer ${token}` },
          }),
        ]);

        if (agentsRes.ok) {
          const agentsData = await agentsRes.json();
          setAgents(agentsData);
        }

        if (clientsRes.ok) {
          const clientsData = await clientsRes.json();
          setClients(clientsData);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    if (token && hasRole(['admin', 'moderator'])) {
      fetchData();
    }
  }, [token, hasRole]);

  // Обработка клавиши Esc для закрытия модального окна
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (onClose) {
          onClose();
        } else if (onCancel) {
          onCancel();
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [onCancel, onClose]);

  // Только админ и модератор могут создавать пользователей
  if (!hasRole(['admin', 'moderator'])) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-700">У вас нет прав для создания пользователей.</p>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/auth/create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось создать пользователя');
      }

      onSuccess?.();
      // Очищаем форму
      setFormData({
        username: '',
        password: '',
        role: 'user',
        agentIds: [],
        clientIds: [],
      });
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Произошла ошибка при создании пользователя');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Если меняется роль на admin или moderator, очищаем назначения
    if (name === 'role' && ['admin', 'moderator'].includes(value)) {
      setFormData(prev => ({
        ...prev,
        [name]: value as UserRole,
        agentIds: [],
        clientIds: []
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'role' ? value as UserRole : value,
      }));
    }
  };

  const handleCheckboxChange = (type: 'agents' | 'clients', id: number) => {
    const fieldName = type === 'agents' ? 'agentIds' : 'clientIds';
    const currentIds = formData[fieldName] || [];

    const newIds = currentIds.includes(id)
      ? currentIds.filter(existingId => existingId !== id)
      : [...currentIds, id];

    setFormData(prev => ({
      ...prev,
      [fieldName]: newIds,
    }));
  };

  // Роли, которые может назначать текущий пользователь
  const availableRoles: { value: UserRole; label: string }[] = [
    { value: 'user', label: 'Пользователь' },
  ];

  if (hasRole(['admin'])) {
    availableRoles.push(
      { value: 'moderator', label: 'Модератор' },
      { value: 'admin', label: 'Администратор' }
    );
  }

  return (
    <div className="max-w-2xl mx-auto bg-white shadow-2xl rounded-3xl p-8">
      {/* Градиентный заголовок */}
      <div className="relative mb-8 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 rounded-2xl p-6 text-white overflow-hidden">
        {/* Декоративные элементы */}
        <div className="absolute top-0 right-0 -mt-4 -mr-16 w-24 h-24 bg-white opacity-10 rounded-full"></div>
        <div className="absolute bottom-0 left-0 -mb-6 -ml-6 w-16 h-16 bg-white opacity-10 rounded-full"></div>

        <div className="relative z-10 flex items-center justify-between">
          <div className="flex items-center">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-3 mr-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-1">
                👥 Создать пользователя
              </h3>
              <p className="text-white text-opacity-90 text-sm">
                ✨ Новый аккаунт с настройкой роли и доступа
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 backdrop-blur-sm rounded-full p-3 transition-all duration-200 hover:scale-110 hover:rotate-90"
              title="Закрыть"
            >
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="username" className="block text-sm font-bold text-gray-700 mb-2">
            👤 Имя пользователя
          </label>
          <input
            type="text"
            id="username"
            name="username"
            required
            className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 disabled:opacity-50"
            value={formData.username}
            onChange={handleChange}
            disabled={loading}
            placeholder="Введите имя пользователя"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-bold text-gray-700 mb-2">
            🔒 Пароль
          </label>
          <input
            type="password"
            id="password"
            name="password"
            required
            className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 disabled:opacity-50"
            value={formData.password}
            onChange={handleChange}
            disabled={loading}
            placeholder="Создайте надёжный пароль"
          />
        </div>

        <div>
          <label htmlFor="role" className="block text-sm font-bold text-gray-700 mb-2">
            🏅 Роль пользователя
          </label>
          <select
            id="role"
            name="role"
            className="block w-full px-4 py-3 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-200 disabled:opacity-50 bg-white"
            value={formData.role}
            onChange={handleChange}
            disabled={loading}
          >
            {availableRoles.map(role => (
              <option key={role.value} value={role.value}>
                {role.value === 'admin' && '👑 '}
                {role.value === 'moderator' && '🛡️ '}
                {role.value === 'user' && '👤 '}
                {role.label}
              </option>
            ))}
          </select>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full p-2">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700">
                  🤖 Назначить агентов
                </label>
                <p className="text-xs text-gray-500">
                  {['admin', 'moderator'].includes(formData.role) ? 'Не требуется для этой роли' : 'Выберите агентов для пользователя'}
                </p>
              </div>
            </div>
            {!['admin', 'moderator'].includes(formData.role) && (
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, agentIds: agents.map(a => a.id) }))}
                  className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-full transition-all duration-200 hover:shadow-md"
                >
                  ✅ Все
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, agentIds: [] }))}
                  className="px-3 py-1 text-xs font-medium text-red-600 bg-red-100 hover:bg-red-200 rounded-full transition-all duration-200 hover:shadow-md"
                >
                  ❌ Сбросить
                </button>
              </div>
            )}
          </div>
          <div className={`max-h-40 overflow-y-auto bg-white rounded-xl border border-gray-200 p-4 space-y-3 ${['admin', 'moderator'].includes(formData.role) ? 'opacity-50' : ''}`}>
            {agents.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-3"></div>
                  <p className="text-sm text-gray-500">Загрузка агентов...</p>
                </div>
              </div>
            ) : (
              agents.map(agent => (
                <label key={agent.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-150 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.agentIds?.includes(agent.id) || false}
                    onChange={() => handleCheckboxChange('agents', agent.id)}
                    disabled={loading || ['admin', 'moderator'].includes(formData.role)}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-all duration-150 disabled:opacity-50"
                  />
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className={`text-sm font-medium ${['admin', 'moderator'].includes(formData.role) ? 'text-gray-400' : 'text-gray-700'}`}>
                      {agent.name}
                    </span>
                  </div>
                </label>
              ))
            )}
          </div>
          <div className="mt-3 px-3 py-2 bg-blue-50 rounded-lg">
            <p className="text-xs text-blue-600 font-medium">
              {['admin', 'moderator'].includes(formData.role)
                ? '👑 Администраторы и модераторы имеют доступ ко всем агентам'
                : `📊 Выбрано: ${formData.agentIds?.length || 0} из ${agents.length} агентов`}
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full p-2">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700">
                  🏢 Назначить клиентов
                </label>
                <p className="text-xs text-gray-500">
                  {['admin', 'moderator'].includes(formData.role) ? 'Не требуется для этой роли' : 'Выберите клиентов для пользователя'}
                </p>
              </div>
            </div>
            {!['admin', 'moderator'].includes(formData.role) && (
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, clientIds: clients.map(c => c.id) }))}
                  className="px-3 py-1 text-xs font-medium text-purple-600 bg-purple-100 hover:bg-purple-200 rounded-full transition-all duration-200 hover:shadow-md"
                >
                  ✅ Все
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, clientIds: [] }))}
                  className="px-3 py-1 text-xs font-medium text-red-600 bg-red-100 hover:bg-red-200 rounded-full transition-all duration-200 hover:shadow-md"
                >
                  ❌ Сбросить
                </button>
              </div>
            )}
          </div>
          <div className={`max-h-40 overflow-y-auto bg-white rounded-xl border border-gray-200 p-4 space-y-3 ${['admin', 'moderator'].includes(formData.role) ? 'opacity-50' : ''}`}>
            {clients.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto mb-3"></div>
                  <p className="text-sm text-gray-500">Загрузка клиентов...</p>
                </div>
              </div>
            ) : (
              clients.map(client => (
                <label key={client.id} className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors duration-150 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.clientIds?.includes(client.id) || false}
                    onChange={() => handleCheckboxChange('clients', client.id)}
                    disabled={loading || ['admin', 'moderator'].includes(formData.role)}
                    className="h-5 w-5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded transition-all duration-150 disabled:opacity-50"
                  />
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                    <span className={`text-sm font-medium ${['admin', 'moderator'].includes(formData.role) ? 'text-gray-400' : 'text-gray-700'}`}>
                      {client.name}
                    </span>
                  </div>
                </label>
              ))
            )}
          </div>
          <div className="mt-3 px-3 py-2 bg-purple-50 rounded-lg">
            <p className="text-xs text-purple-600 font-medium">
              {['admin', 'moderator'].includes(formData.role)
                ? '👑 Администраторы и модераторы имеют доступ ко всем клиентам'
                : `📊 Выбрано: ${formData.clientIds?.length || 0} из ${clients.length} клиентов`}
            </p>
          </div>
        </div>

        {error && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-4">
            <div className="flex items-center space-x-3">
              <div className="bg-red-500 rounded-full p-2 flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-red-800">⚠️ Ошибка создания пользователя</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex space-x-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 relative overflow-hidden bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-700 hover:via-teal-700 hover:to-cyan-700 text-white py-4 px-8 rounded-2xl font-bold text-lg shadow-2xl hover:shadow-emerald-500/25 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:transform-none disabled:hover:shadow-2xl"
          >
            <div className="absolute inset-0 bg-white opacity-0 hover:opacity-10 transition-opacity duration-300"></div>
            <div className="relative flex items-center justify-center space-x-2">
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>⏳ Создание пользователя...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                  <span>✨ Создать пользователя</span>
                </>
              )}
            </div>
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 relative overflow-hidden bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 py-4 px-8 rounded-2xl font-bold text-lg shadow-xl hover:shadow-gray-500/25 border-2 border-gray-300 transform hover:scale-105 transition-all duration-300"
            >
              <div className="absolute inset-0 bg-gray-600 opacity-0 hover:opacity-5 transition-opacity duration-300"></div>
              <div className="relative flex items-center justify-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span>❌ Отмена</span>
              </div>
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default CreateUserForm;